import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import { usePermissions } from '../hooks/usePermissions';
import FileDropZone from '../components/FileDropZone';

// 만료일(계약종료일) D-Day 계산/표시 — 업무관리(Tasks.js)의 DdayBadge와 동일한 CSS/라벨 규칙을 그대로 적용
function calcDday(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

function DdayBadge({ date }) {
  const d = calcDday(date);
  if (d === null || isNaN(d)) return null;
  const label = d < 0 ? `${Math.abs(d)}일 초과` : d === 0 ? 'D-Day' : `D-${d}`;
  const color = d < 0 ? '#fff' : d <= 3 ? '#d97706' : '#16a34a';
  const bg    = d < 0 ? '#dc2626' : d <= 3 ? '#fef3c7' : '#dcfce7';
  return <span style={{ fontSize:13, padding:'3px 13px', borderRadius:20, background:bg, color, fontWeight:600 }}>{label}</span>;
}

// 목록 컬럼명 클릭 정렬(오름/내림차순 토글) 공용 정렬 함수 — 숫자면 숫자비교, 아니면 문자열비교.
function sortRows(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    let va = a ? a[sortKey] : null;
    let vb = b ? b[sortKey] : null;
    if (va === null || va === undefined) va = '';
    if (vb === null || vb === undefined) vb = '';
    let cmp;
    const na = Number(va), nb = Number(vb);
    if (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
    else cmp = String(va).localeCompare(String(vb), 'ko');
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
// 목록 헤더 <span> — 컬럼명을 클릭할 때마다 오름차순/내림차순이 토글된다(활성 컬럼만 색상 강조).
function SortableTh({ label, sortKey, activeKey, dir, onClick, style }) {
  const active = activeKey === sortKey;
  return (
    <span onClick={() => onClick(sortKey)}
      title={active ? (dir === 'asc' ? '오름차순 (클릭시 내림차순)' : '내림차순 (클릭시 오름차순)') : '클릭하여 정렬'}
      style={{ ...style, cursor:'pointer', userSelect:'none', color: active ? '#185FA5' : (style?.color), fontWeight: active ? 700 : (style?.fontWeight) }}>
      {label}
    </span>
  );
}

// 구분(계약의 종류) 배지 색상 — 목록등록으로 등록되는 이름은 자유 텍스트라 미리 다 정할 수 없으므로,
// 이름을 해시해서 팔레트 중 하나를 고정 배정한다(같은 이름은 항상 같은 색).
const TAB_PALETTE = [
  { color:'#185FA5', bg:'#E6F1FB' }, { color:'#0F6E56', bg:'#E1F5EE' },
  { color:'#854F0B', bg:'#FAEEDA' }, { color:'#993556', bg:'#FBEAF0' },
  { color:'#6d28d9', bg:'#EDE4FB' }, { color:'#DC2626', bg:'#FEE2E2' },
  { color:'#0EA5E9', bg:'#E0F2FE' }, { color:'#DB2777', bg:'#FCE7F3' },
];
function tabStyleOf(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return TAB_PALETTE[hash % TAB_PALETTE.length];
}

// 결재방법 4가지 — 2회분할/3회분할을 고르면 그 횟수만큼 결재시기/결재금액/결재비율 입력행이 나타남
const PAYMENT_METHODS = ['선불-일시납', '후불-일시납', '2회분할', '3회분할'];
const installmentCountOf = (method) => (method === '2회분할' ? 2 : method === '3회분할' ? 3 : (method ? 1 : 0));

// 계약금액 등 숫자 입력에 1000단위 콤마를 자동 적용하기 위한 헬퍼
const onlyDigits  = (s) => String(s || '').replace(/[^\d]/g, '');
const formatComma = (s) => { const d = onlyDigits(s); return d ? Number(d).toLocaleString('ko-KR') : ''; };
const parseAmount = (s) => Number(onlyDigits(s)) || 0;

// 결재시기(분할납부 내역, JSON 문자열)를 목록에 요약 표시할 때 사용
function paymentScheduleSummary(json) {
  if (!json) return '-';
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr) || !arr.length) return '-';
    const first = arr[0].schedule || '-';
    return arr.length > 1 ? `${first} 외 ${arr.length - 1}건` : first;
  } catch (e) { return '-'; }
}
function parseInstallments(json) {
  if (!json) return [];
  try { const arr = JSON.parse(json); return Array.isArray(arr) ? arr : []; } catch (e) { return []; }
}

// 계약금액 — 공급가액/세액/합계 3분할로 저장(amount 컬럼에 JSON 문자열). 예전 데이터(단순 숫자
// 텍스트)와도 호환되도록, JSON 파싱에 실패하면 그 값을 합계로만 취급한다.
function parseAmountBreakdown(raw) {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object') return obj;
  } catch (e) { /* 하위호환: 예전 단순 숫자 텍스트 */ }
  return { supply:'', tax:'', total: raw };
}
function amountTotalDisplay(raw) {
  const b = parseAmountBreakdown(raw);
  return b && b.total ? `${b.total}원` : '-';
}

// 이름 비교용 정규화 — 업무관리(Tasks.js)와 동일한 방식
const normName = (s) => String(s || '').replace(/\s+/g, '').trim();
// 담당자 표시: 회원목록에서 이름이 일치하는 사용자를 찾아 "직급 이름"으로 표시 (매칭 안되면 이름만)
function formatManager(name, userList) {
  if (!name) return '';
  const u = (userList || []).find(x => normName(x.name) === normName(name));
  return (u && u.position) ? `${u.position} ${name}` : name;
}
// 담당부서 표시: 회원목록에서 이름이 일치하는 사용자를 찾아 그 사용자의 부서를 표시 (매칭 안되면 '-')
function formatDepartment(name, userList) {
  if (!name) return '';
  const u = (userList || []).find(x => normName(x.name) === normName(name));
  return (u && u.department) ? u.department : '-';
}

const navBtnStyle = { background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' };

export default function Contracts() {
  const [contracts, setContracts]   = useState([]);
  const [categories, setCategories] = useState([]);   // 목록등록 - 구분 탭 목록
  const [selected, setSelected]     = useState(null); // 상세 보기
  const [showForm, setShowForm]     = useState(false); // 계약체결(등록/수정) 폼
  const [editData, setEditData]     = useState(null);  // 수정 시 기존 데이터
  const [showCategoryModal, setShowCategoryModal] = useState(false); // 목록등록 모달
  const [msg, setMsg] = useState('');

  const perm = usePermissions();
  const { hasDetail, canWrite: canWriteMenu, hasListItem } = perm;
  const canDownload       = hasDetail('contracts', 'download_allowed');
  const canManageCategory = hasDetail('contracts', 'category_manage');
  const canWriteContracts = canWriteMenu('contracts');
  // 선택된 계약(상세보기)에 대해 실제로 수정/삭제 가능한지 — 등록된 목록별 쓰기권한(category_write_scope) 검사
  const canEditSelected   = canWriteContracts && !!selected && hasListItem('contracts', 'category_write_scope', selected.contract_type);
  const canDeleteSelected = canWriteContracts && !!selected && hasListItem('contracts', 'category_write_scope', selected.contract_type);

  const [userList, setUserList] = useState([]); // 담당부서 표시용(담당자 이름 -> 부서 매칭)

  const load = () => API.get('/contracts').then(r => setContracts(r.data)).catch(() => {});
  const loadCategories = () => API.get('/contracts/categories').then(r => setCategories(r.data)).catch(() => {});
  useEffect(() => { load(); loadCategories(); API.get('/contracts/users/list').then(r => setUserList(r.data)).catch(() => {}); }, []);

  // 상태바(최신 업데이트 현황)에서 계약관리 항목을 클릭해 들어온 경우 그 항목의 상세 모달을 바로 연다.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const ao = location.state?.activityOpen;
    if (ao && contracts.length) {
      const found = contracts.find(c => String(c.id) === String(ao.itemId));
      if (found) setSelected(found);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, contracts]); // eslint-disable-line

  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(''), 3000);
    return () => clearTimeout(t);
  }, [msg]);

  // ── 탭메뉴(전체보기 + 목록등록된 구분들) ──────────────────────────
  const [activeTab, setActiveTab] = useState('전체');

  // ── 페이지당 표시개수 ─────────────────────────────────────────
  const [pageSize, setPageSize] = useState('all');   // 10 / 30 / 50 / 100 / 'all'
  const [page, setPage] = useState(1);

  // ── 연/월 선택(달력) — 업무관리(Tasks.js) 방식을 그대로 따름. 등록일(created_at) 기준 ──
  const now = new Date();
  const [selYear, setSelYear]   = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(now.getFullYear());
  const monthPickerRef = useRef(null);

  useEffect(() => { if (showMonthPicker) setPickerYear(selYear); }, [showMonthPicker, selYear]);
  useEffect(() => {
    if (!showMonthPicker) return;
    const onClickOutside = (e) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) setShowMonthPicker(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showMonthPicker]);

  const shiftMonth = (delta) => {
    if (!selMonth) return; // '전체'(연간) 보기 중에는 좌우 이동 비활성화
    let m = selMonth + delta, y = selYear;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setSelMonth(m); setSelYear(y);
  };

  // ── 검색 ─────────────────────────────────────────────────────
  const [search, setSearch] = useState('');

  // 목록 컬럼명 클릭 정렬 — null(기본)이면 기존과 동일하게 등록 순서 그대로 유지된다.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = contracts.filter(c => {
    const matchTab = activeTab === '전체' || c.contract_type === activeTab;
    const matchMonth = (() => {
      if (!c.created_at) return true;
      const d = new Date(c.created_at);
      if (isNaN(d)) return true;
      const y = d.getFullYear(), m = d.getMonth() + 1;
      return selMonth ? (y === selYear && m === selMonth) : (y === selYear);
    })();
    const matchSearch = !search ||
      (c.contract_type || '').includes(search) ||
      (c.contract_name || '').includes(search) ||
      (c.contract_no || '').includes(search) ||
      (c.counterparty || '').includes(search) ||
      (c.manager || '').includes(search);
    return matchTab && matchMonth && matchSearch;
  });

  useEffect(() => { setPage(1); }, [activeTab, selYear, selMonth, search, pageSize, sortKey, sortDir]);

  const sortedFiltered = sortRows(filtered, sortKey, sortDir);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(sortedFiltered.length / pageSize));
  const pagedList  = pageSize === 'all' ? sortedFiltered : sortedFiltered.slice((page - 1) * pageSize, page * pageSize);

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete(`/contracts/${id}`);
    setSelected(null);
    setMsg('삭제 완료!');
    load();
  };

  const handleEdit = (contract) => {
    setEditData(contract);
    setShowForm(true);
    setSelected(null);
  };

  return (
    <div style={{ padding:24, maxWidth:1690 }}>

      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>📄 계약관리</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>매입·매출·외주 등 각종 계약을 등록하고 계약기간을 관리합니다.</p>
        </div>
        {(canManageCategory || canWriteContracts) && (
          <div className="hdr-actions" style={{ display:'flex', gap:8 }}>
            {canManageCategory && (
              <button onClick={() => setShowCategoryModal(true)}
                style={{ background:'#475569', color:'#fff', border:'none', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                📋 목록등록
              </button>
            )}
            {canWriteContracts && (
              <button onClick={() => { setShowForm(true); setEditData(null); }}
                style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                ✍️ 계약등록
              </button>
            )}
          </div>
        )}
      </div>

      {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      {/* 탭메뉴(전체보기 + 구분별) + 우측 컨트롤(표시개수/달력/검색) */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16, paddingBottom:10, flexWrap:'wrap', gap:10, borderBottom:'1px solid #e5e7eb' }}>
        <div style={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
          {['전체', ...categories.map(c => c.name)].map(name => {
            const count = name === '전체' ? contracts.length : contracts.filter(c => c.contract_type === name).length;
            const active = activeTab === name;
            const style = name === '전체' ? { color:'#475569', bg:'#E2E8F0' } : tabStyleOf(name);
            return (
              <button key={name} onClick={() => setActiveTab(name)}
                style={{ padding:'10px 18px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
                  borderBottom: active ? '2px solid #185FA5' : '2px solid #e5e7eb',
                  background:'none', color: active ? '#185FA5' : '#888' }}>
                {name}
                {count > 0 && (
                  <span style={{ fontSize:11, marginLeft:6, background: active ? '#185FA5' : '#e5e7eb',
                    color: active ? '#fff' : '#888', padding:'1px 7px', borderRadius:20 }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          {/* 검색창 */}
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 계약번호, 계약상대, 담당자 검색..."
            style={{ padding:'8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, width:220, boxSizing:'border-box' }} />

          {/* 연/월 선택 */}
          <div ref={monthPickerRef} style={{ position:'relative', display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={() => shiftMonth(-1)} disabled={!selMonth}
              style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:28, height:28,
                fontSize:13, cursor: selMonth ? 'pointer' : 'not-allowed', color: selMonth ? '#555' : '#ccc' }}>◀</button>
            <span onClick={() => setShowMonthPicker(v => !v)}
              style={{ fontSize:16, fontWeight:700, color:'#000', minWidth:100, textAlign:'center', cursor:'pointer' }}>
              {selYear}년 {selMonth ? `${selMonth}월` : '전체'}
            </span>
            <button onClick={() => shiftMonth(1)} disabled={!selMonth}
              style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:28, height:28,
                fontSize:13, cursor: selMonth ? 'pointer' : 'not-allowed', color: selMonth ? '#555' : '#ccc' }}>▶</button>
            {!(selYear === now.getFullYear() && selMonth === now.getMonth() + 1) && (
              <button onClick={() => { setSelYear(now.getFullYear()); setSelMonth(now.getMonth() + 1); }}
                style={{ fontSize:12, padding:'5px 10px', background:'#E6F1FB', color:'#185FA5',
                  border:'1px solid #185FA5', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                이번달
              </button>
            )}

            {showMonthPicker && (
              <div style={{ position:'absolute', top:'calc(100% + 6px)', right:0, background:'#fff',
                border:'1px solid #e5e7eb', borderRadius:10, boxShadow:'0 4px 14px rgba(0,0,0,0.12)',
                padding:12, zIndex:20, width:220 }}
                onClick={e => e.stopPropagation()}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <button onClick={() => setPickerYear(y => y - 1)} style={navBtnStyle}>◀</button>
                  <span style={{ fontWeight:600, fontSize:14 }}>{pickerYear}년</span>
                  <button onClick={() => setPickerYear(y => y + 1)} style={navBtnStyle}>▶</button>
                </div>
                <button
                  onClick={() => { setSelYear(pickerYear); setSelMonth(null); setShowMonthPicker(false); }}
                  style={{ width:'100%', padding:'6px 0', marginBottom:6, borderRadius:6, border:'1px solid',
                    borderColor: (pickerYear === selYear && !selMonth) ? '#185FA5' : '#ddd',
                    background:  (pickerYear === selYear && !selMonth) ? '#E6F1FB' : '#fff',
                    color:       (pickerYear === selYear && !selMonth) ? '#185FA5' : '#555',
                    fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  {pickerYear}년 전체보기
                </button>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
                  {Array.from({ length:12 }, (_, i) => i + 1).map(m => {
                    const active = pickerYear === selYear && m === selMonth;
                    return (
                      <button key={m}
                        onClick={() => { setSelYear(pickerYear); setSelMonth(m); setShowMonthPicker(false); }}
                        style={{ padding:'6px 0', borderRadius:6, border:'1px solid',
                          borderColor: active ? '#185FA5' : '#ddd',
                          background:  active ? '#E6F1FB' : '#fff',
                          color:       active ? '#185FA5' : '#555',
                          fontSize:14, cursor:'pointer' }}>
                        {m}월
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* 페이지당 표시개수 */}
          <select value={pageSize} onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ padding:'7px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, color:'#555',
              outline:'none', cursor:'pointer', background:'#fff' }}>
            <option value="10">10개씩</option>
            <option value="30">30개씩</option>
            <option value="50">50개씩</option>
            <option value="100">100개씩</option>
            <option value="all">모두보기</option>
          </select>
        </div>
      </div>

      {/* 목록이 비어있을 때 */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#aaa', fontSize:14 }}>
          등록된 계약이 없습니다.<br />
          {canWriteContracts && <span style={{ fontSize:13 }}>상단 "✍️ 계약등록" 버튼을 눌러 추가하세요.</span>}
        </div>
      ) : (
        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', overflowX:'auto', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display:'grid', gridTemplateColumns:'90px 110px 1fr 1fr 120px 100px 110px 100px 100px 80px 90px 90px 100px',
            background:'#f8fafc', padding:'10px 10px', fontSize:14, fontWeight:600,
            color:'#555', borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
            {/* 목록 컬럼명을 클릭하면 클릭할 때마다 오름차순/내림차순이 토글된다. 계약금액(분할
                저장된 값)/결재시기/D-Day(계산값)/담당부서(파생값)는 정렬 대상에서 제외한다. */}
            <SortableTh label="계약구분" sortKey="contract_type" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="계약번호" sortKey="contract_no" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="계약상대" sortKey="counterparty" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={{ textAlign:'left' }} />
            <SortableTh label="계약명" sortKey="contract_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={{ textAlign:'left' }} />
            <span>계약금액</span>
            <SortableTh label="결재방법" sortKey="payment_method" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <span>결재시기</span>
            <SortableTh label="시작일" sortKey="start_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="종료일" sortKey="end_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <span>D-Day</span>
            <span>담당부서</span>
            <SortableTh label="담당자" sortKey="manager" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="등록일" sortKey="created_at" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
          </div>
          {pagedList.map((c, i) => {
            const style = tabStyleOf(c.contract_type);
            return (
              <div key={c.id} onClick={() => setSelected(c)}
                style={{ display:'grid', gridTemplateColumns:'90px 110px 1fr 1fr 120px 100px 110px 100px 100px 80px 90px 90px 100px',
                  padding:'10px 10px', fontSize:14, textAlign:'center', alignItems:'center',
                  borderBottom: i < pagedList.length - 1 ? '1px solid #f0f0f0' : 'none',
                  cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                <span>
                  <span style={{ fontSize:13, padding:'3px 10px', borderRadius:20, fontWeight:600, background:style.bg, color:style.color }}>
                    {c.contract_type}
                  </span>
                </span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.contract_no || '-'}</span>
                <span style={{ textAlign:'left', color:'#666666', fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.counterparty || '-'}</span>
                <span style={{ textAlign:'left', color:'#666666', fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.contract_name || '-'}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{amountTotalDisplay(c.amount)}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.payment_method || '-'}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{paymentScheduleSummary(c.payment_schedule)}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.start_date || '-'}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.end_date || '-'}</span>
                <span><DdayBadge date={c.end_date} /></span>
                <span style={{ color:'#666666', fontSize:14 }}>{formatDepartment(c.manager, userList)}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{formatManager(c.manager, userList) || '-'}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.created_at?.slice(0, 10) || '-'}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* 페이지네이션 (모두보기가 아닐 때만) */}
      {pageSize !== 'all' && totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:14 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page === 1 ? '#ccc' : '#555', fontSize:14, cursor: page === 1 ? 'not-allowed' : 'pointer' }}>◀</button>
          <span style={{ fontSize:14, color:'#666' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page === totalPages ? '#ccc' : '#555', fontSize:14, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>▶</button>
        </div>
      )}

      {/* 상세 모달 */}
      {selected && (() => {
        const style = tabStyleOf(selected.contract_type);
        return (
          <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
            onClick={() => setSelected(null)}>
            <div style={{ background:'#fff', borderRadius:16, padding:28, width:480, maxWidth:'90vw', maxHeight:'80vh', overflowY:'auto' }}
              onClick={e => e.stopPropagation()}>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                <span style={{ fontSize:13, padding:'4px 12px', borderRadius:20, fontWeight:600, background:style.bg, color:style.color }}>
                  {selected.contract_type}
                </span>
                <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
              </div>
              {selected.contract_name && <h3 style={{ fontSize:17, fontWeight:600, margin:'0 0 16px' }}>{selected.contract_name}</h3>}

              {[
                ['계약번호', selected.contract_no],
                ['계약상대', selected.counterparty],
                ['결재방법', selected.payment_method],
                ['시작일', selected.start_date],
                ['종료일', selected.end_date],
                ['담당부서', formatDepartment(selected.manager, userList)],
                ['담당자', formatManager(selected.manager, userList)],
                ['등록자', selected.created_by],
                ['등록일', selected.created_at?.slice(0, 10)],
              ].map(([label, val]) => val ? (
                <div key={label} style={{ display:'flex', gap:12, marginBottom:10, fontSize:14 }}>
                  <span style={{ width:80, color:'#888', flexShrink:0 }}>{label}</span>
                  <span style={{ color:'#1a1a1a', fontWeight: label === '종료일' ? 600 : 400 }}>
                    {val} {label === '종료일' && <DdayBadge date={val} />}
                  </span>
                </div>
              ) : null)}

              {parseAmountBreakdown(selected.amount) && (
                <div style={{ marginTop:2, marginBottom:16 }}>
                  <div style={{ fontSize:13, color:'#888', marginBottom:6 }}>계약금액</div>
                  <div style={{ border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', background:'#f8fafc',
                      padding:'6px 10px', fontSize:12, fontWeight:600, color:'#555', textAlign:'center' }}>
                      <span>공급가액</span><span>세액</span><span>합계</span>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr',
                      padding:'6px 10px', fontSize:13, textAlign:'center', color:'#1a1a1a', borderTop:'1px solid #f0f0f0' }}>
                      <span>{parseAmountBreakdown(selected.amount).supply ? `${parseAmountBreakdown(selected.amount).supply}원` : '-'}</span>
                      <span>{parseAmountBreakdown(selected.amount).tax ? `${parseAmountBreakdown(selected.amount).tax}원` : '-'}</span>
                      <span style={{ fontWeight:600 }}>{parseAmountBreakdown(selected.amount).total ? `${parseAmountBreakdown(selected.amount).total}원` : '-'}</span>
                    </div>
                  </div>
                </div>
              )}

              {parseInstallments(selected.payment_schedule).length > 0 && (
                <div style={{ marginTop:6, marginBottom:16 }}>
                  <div style={{ fontSize:13, color:'#888', marginBottom:6 }}>결재시기</div>
                  <div style={{ border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 70px', background:'#f8fafc',
                      padding:'6px 10px', fontSize:12, fontWeight:600, color:'#555', textAlign:'center' }}>
                      <span>결재시기</span><span>결재금액</span><span>비율</span>
                    </div>
                    {parseInstallments(selected.payment_schedule).map((row, i) => (
                      <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 70px',
                        padding:'6px 10px', fontSize:13, textAlign:'center', color:'#1a1a1a',
                        borderTop:'1px solid #f0f0f0' }}>
                        <span>{row.schedule || '-'}</span>
                        <span>{row.amount ? `${row.amount}원` : '-'}</span>
                        <span>{row.ratio ? `${row.ratio}%` : '-'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.file_name && canDownload && (
                <div style={{ marginTop:16, padding:'12px 14px', background:'#f0f6ff', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:13, color:'#185FA5' }}>📎 {selected.file_ori}</span>
                  <a href={`http://192.168.90.36:3001/api/contracts/download/${selected.file_name}`}
                    style={{ background:'#185FA5', color:'#fff', padding:'6px 14px', borderRadius:6, fontSize:13, textDecoration:'none' }}>
                    다운로드
                  </a>
                </div>
              )}

              {(canEditSelected || canDeleteSelected) && (
                <div style={{ display:'flex', gap:8, marginTop:20 }}>
                  {canEditSelected && (
                    <button onClick={() => handleEdit(selected)}
                      style={{ flex:1, padding:'10px', background:'#f0f6ff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
                      ✏️ 수정
                    </button>
                  )}
                  {canDeleteSelected && (
                    <button onClick={() => handleDelete(selected.id)}
                      style={{ flex:1, padding:'10px', background:'#fee2e2', color:'#dc2626', border:'1px solid #dc2626', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
                      🗑 삭제
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* 계약체결(등록/수정) 모달 */}
      {showForm && (
        <ContractForm
          editData={editData}
          categories={categories}
          hasListItem={hasListItem}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSaved={() => { setShowForm(false); setEditData(null); load(); setMsg(editData ? '수정 완료!' : '등록 완료!'); }}
        />
      )}

      {/* 목록등록(구분 관리) 모달 */}
      {showCategoryModal && (
        <CategoryModal
          categories={categories}
          onClose={() => setShowCategoryModal(false)}
          onChanged={loadCategories}
        />
      )}
    </div>
  );
}

// ── 계약정보 등록(등록/수정) 폼 컴포넌트 ──────────────────────────
function ContractForm({ editData, categories, hasListItem, onClose, onSaved }) {
  const initAmount = parseAmountBreakdown(editData?.amount);
  const [form, setForm] = useState({
    contract_type:    editData?.contract_type    || (categories[0]?.name || ''),
    contract_name:    editData?.contract_name    || '',
    contract_no:      editData?.contract_no      || '',
    counterparty:     editData?.counterparty     || '',
    payment_method:   editData?.payment_method   || '',
    start_date:       editData?.start_date       || '',
    end_date:         editData?.end_date         || '',
    manager:          editData?.manager          || '',
  });
  // 계약금액 — 공급가액 / 세액(자동계산, 10%) / 합계. 둘 중 하나(공급가액 또는 합계)를 입력하면
  // 나머지 두 값을 자동으로 계산한다. 세액은 항상 자동계산 값이라 직접 입력하지 않는다.
  const [supply, setSupply] = useState(formatComma(initAmount?.supply || ''));
  const [tax, setTax]       = useState(formatComma(initAmount?.tax || ''));
  const [total, setTotal]   = useState(formatComma(initAmount?.total || ''));
  // 결재방법(선불-일시납/후불-일시납/2회분할/3회분할)에 따라 결재시기/결재금액/결재비율 입력행이 늘어남
  const [installments, setInstallments] = useState(() => {
    const parsed = parseInstallments(editData?.payment_schedule);
    if (parsed.length) return parsed;
    const count = installmentCountOf(editData?.payment_method);
    return Array.from({ length: count }, () => ({ schedule:'', amount:'', ratio:'' }));
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // 총액(합계) 기준으로 결재행 금액/비율을 다시 맞춰준다 — 결재비율이 이미 입력된 행은 비율을
  // 유지한 채 금액만 새 총액으로 재계산한다.
  const resyncInstallments = (totalNum) => {
    setInstallments(prev => prev.map(row => {
      if (!row.ratio) return row;
      const amt = Math.round(totalNum * Number(row.ratio) / 100);
      return { ...row, amount: totalNum ? formatComma(String(amt)) : row.amount };
    }));
  };

  const handleSupplyChange = (e) => {
    const digits = onlyDigits(e.target.value);
    const supplyNum = Number(digits) || 0;
    const taxNum = Math.round(supplyNum * 0.1);
    const totalNum = supplyNum + taxNum;
    setSupply(formatComma(digits));
    setTax(digits ? formatComma(String(taxNum)) : '');
    setTotal(digits ? formatComma(String(totalNum)) : '');
    resyncInstallments(totalNum);
  };

  const handleTotalChange = (e) => {
    const digits = onlyDigits(e.target.value);
    const totalNum = Number(digits) || 0;
    const supplyNum = Math.round(totalNum / 1.1);
    const taxNum = totalNum - supplyNum;
    setTotal(formatComma(digits));
    setSupply(digits ? formatComma(String(supplyNum)) : '');
    setTax(digits ? formatComma(String(taxNum)) : '');
    resyncInstallments(totalNum);
  };

  const handlePaymentMethodChange = (e) => {
    const method = e.target.value;
    const count = installmentCountOf(method);
    setForm(f => ({ ...f, payment_method: method }));
    setInstallments(prev => Array.from({ length: count }, (_, i) => prev[i] || { schedule:'', amount:'', ratio:'' }));
  };

  const totalRatioSum = installments.reduce((sum, r) => sum + (Number(r.ratio) || 0), 0);

  const handleInstallmentField = (idx, field, value) => {
    const totalNum = parseAmount(total);
    setInstallments(prev => prev.map((row, i) => {
      if (i !== idx) return row;
      if (field === 'schedule') return { ...row, schedule: value };
      if (field === 'amount') {
        const digits = onlyDigits(value);
        const amountNum = Number(digits) || 0;
        const ratio = totalNum > 0 ? ((amountNum / totalNum) * 100).toFixed(1) : row.ratio;
        return { ...row, amount: formatComma(digits), ratio };
      }
      if (field === 'ratio') {
        const ratioNum = Number(value) || 0;
        const amountNum = totalNum > 0 ? Math.round(totalNum * ratioNum / 100) : null;
        return { ...row, ratio: value, amount: amountNum !== null ? formatComma(String(amountNum)) : row.amount };
      }
      return row;
    }));
  };

  // 현재 선택된 구분(contract_type)에 대해 이 계정이 실제로 등록/수정 가능한지 — 등록된 목록별
  // 쓰기권한(category_write_scope) 검사. 백엔드가 최종적으로 막아주지만, 프론트에서도 미리 막아
  // 불필요한 요청/에러 메시지를 줄인다.
  const canSubmitType = !hasListItem || hasListItem('contracts', 'category_write_scope', form.contract_type);

  const handleSubmit = async () => {
    if (!form.contract_type) return alert('계약구분을 선택해주세요.');
    if (!form.contract_name) return alert('계약명을 입력해주세요.');
    if (!canSubmitType) return alert('이 구분(계약의 종류)에 대한 등록/수정 권한이 없습니다. 최고관리자에게 문의해주세요.');
    setLoading(true);

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    // 계약금액(공급가액/세액/합계)은 amount 컬럼에 JSON으로 저장(다른 구조화 필드와 동일한 방식)
    const amountJson = (supply || tax || total) ? JSON.stringify({ supply, tax, total }) : '';
    fd.append('amount', amountJson);
    // 콤마 표시용 문자열은 그대로 저장(다른 금액 필드들과 동일한 방식)
    const cleanInstallments = installments
      .filter(r => r.schedule || r.amount || r.ratio)
      .map(r => ({ schedule: r.schedule, amount: onlyDigits(r.amount) ? formatComma(r.amount) : '', ratio: r.ratio }));
    fd.append('payment_schedule', cleanInstallments.length ? JSON.stringify(cleanInstallments) : '');
    if (file) fd.append('file', file);

    try {
      if (editData) {
        await API.put(`/contracts/${editData.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await API.post('/contracts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' };
  const labelStyle = { fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:4 };
  const smallInputStyle = { width:'100%', padding:'7px 8px', border:'1px solid #ddd', borderRadius:6, fontSize:13, boxSizing:'border-box', outline:'none' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:520, maxWidth:'90vw', maxHeight:'85vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>{editData ? '계약 수정' : '계약정보 등록'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={labelStyle}>계약구분 *</label>
            {categories.length === 0 ? (
              <div style={{ ...inputStyle, color:'#dc2626', background:'#fef2f2', fontSize:12 }}>
                등록된 목록이 없습니다. "📋 목록등록"에서 먼저 등록해주세요.
              </div>
            ) : (
              <select name="contract_type" value={form.contract_type} onChange={handleChange} style={inputStyle}>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            )}
          </div>
          <div>
            <label style={labelStyle}>계약번호</label>
            <input name="contract_no" placeholder="예: CT-2026-001" value={form.contract_no} onChange={handleChange} style={inputStyle} />
          </div>
        </div>

        <label style={labelStyle}>계약명 *</label>
        <input name="contract_name" placeholder="예: 2026년 원자재 공급계약" value={form.contract_name} onChange={handleChange} style={inputStyle} />

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={labelStyle}>계약상대</label>
            <input name="counterparty" placeholder="거래처명" value={form.counterparty} onChange={handleChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>담당자</label>
            <input name="manager" placeholder="담당자명" value={form.manager} onChange={handleChange} style={inputStyle} />
          </div>
        </div>

        <label style={labelStyle}>계약금액</label>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
          <div>
            <div style={{ fontSize:11, color:'#888', marginBottom:3 }}>공급가액</div>
            <input value={supply} onChange={handleSupplyChange} placeholder="0" style={{ ...smallInputStyle, padding:'9px 10px' }} inputMode="numeric" />
          </div>
          <div>
            <div style={{ fontSize:11, color:'#888', marginBottom:3 }}>세액</div>
            <input value={tax} readOnly placeholder="0" style={{ ...smallInputStyle, padding:'9px 10px', background:'#f5f5f5', color:'#888' }} />
          </div>
          <div>
            <div style={{ fontSize:11, color:'#888', marginBottom:3 }}>합계</div>
            <input value={total} onChange={handleTotalChange} placeholder="0" style={{ ...smallInputStyle, padding:'9px 10px' }} inputMode="numeric" />
          </div>
        </div>

        <label style={labelStyle}>결재방법</label>
        <select name="payment_method" value={form.payment_method} onChange={handlePaymentMethodChange} style={inputStyle}>
          <option value="">선택안함</option>
          {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        {installments.length > 0 && (
          <div style={{ marginBottom:12 }}>
            <div style={{ display:'flex', justifyContent:'flex-end', fontSize:12, color: totalRatioSum === 100 ? '#0F6E56' : '#888', marginBottom:6, fontWeight:600 }}>
              합산 비율: {totalRatioSum}%
            </div>
            <div style={{ display:'grid', gridTemplateColumns: installments.length > 1 ? '46px 1fr 1fr 70px' : '1fr 1fr 70px',
              gap:6, fontSize:12, color:'#888', marginBottom:4 }}>
              {installments.length > 1 && <span></span>}
              <span>결재시기</span><span>결재금액</span><span>비율(%)</span>
            </div>
            {installments.map((row, idx) => (
              <div key={idx} style={{ display:'grid', gridTemplateColumns: installments.length > 1 ? '46px 1fr 1fr 70px' : '1fr 1fr 70px',
                gap:6, marginBottom:6, alignItems:'center' }}>
                {installments.length > 1 && <span style={{ fontSize:12, color:'#555', textAlign:'center' }}>{idx + 1}차</span>}
                <input type="date" value={row.schedule} onChange={e => handleInstallmentField(idx, 'schedule', e.target.value)} style={smallInputStyle} />
                <input value={row.amount} onChange={e => handleInstallmentField(idx, 'amount', e.target.value)} placeholder="금액" style={smallInputStyle} inputMode="numeric" />
                <input value={row.ratio} onChange={e => handleInstallmentField(idx, 'ratio', e.target.value)} placeholder="%" style={smallInputStyle} inputMode="decimal" />
              </div>
            ))}
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={labelStyle}>시작일</label>
            <input name="start_date" type="date" value={form.start_date} onChange={handleChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>종료일</label>
            <input name="end_date" type="date" value={form.end_date} onChange={handleChange} style={inputStyle} />
          </div>
        </div>

        <label style={labelStyle}>첨부파일 {editData?.file_ori && <span style={{ color:'#185FA5', fontSize:12 }}>(현재: {editData.file_ori})</span>}</label>
        <FileDropZone fileName={file?.name} onFile={setFile} />

        {!canSubmitType && (
          <div style={{ fontSize:12, color:'#dc2626', marginBottom:8 }}>
            "{form.contract_type}" 구분에 대한 등록/수정 권한이 없습니다. 최고관리자에게 문의해주세요.
          </div>
        )}
        <button onClick={handleSubmit} disabled={loading || categories.length === 0 || !canSubmitType}
          style={{ width:'100%', padding:'12px', marginTop:8,
            background: (loading || categories.length === 0 || !canSubmitType) ? '#aaa' : '#185FA5', color:'#fff', border:'none', borderRadius:8,
            fontSize:15, fontWeight:600, cursor: (loading || categories.length === 0 || !canSubmitType) ? 'not-allowed' : 'pointer' }}>
          {loading ? '저장 중...' : (editData ? '수정 완료' : '계약정보 등록')}
        </button>
      </div>
    </div>
  );
}

// ── 목록등록(구분 관리) 모달 컴포넌트 ──────────────────────────
// 등록/삭제뿐 아니라 순서변경(▲▼)과 이름수정(인라인)까지 지원한다.
function CategoryModal({ categories, onClose, onChanged }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const handleAdd = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      await API.post('/contracts/categories', { name: trimmed });
      setName('');
      onChanged();
    } catch (err) {
      setMsg(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 목록을 삭제하시겠습니까?')) return;
    try {
      await API.delete(`/contracts/categories/${id}`);
      onChanged();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditingName(c.name); };
  const cancelEdit = () => { setEditingId(null); setEditingName(''); };
  const saveEdit = async (id) => {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    try {
      await API.put(`/contracts/categories/${id}`, { name: trimmed });
      cancelEdit();
      onChanged();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    }
  };

  const handleMove = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= categories.length) return;
    const order = categories.map(c => c.id);
    [order[index], order[target]] = [order[target], order[index]];
    try {
      await API.put('/contracts/categories/reorder', { order });
      onChanged();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    }
  };

  const inputStyle = { flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:440, maxWidth:'90vw', maxHeight:'80vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>📋 목록등록 (계약구분 관리)</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          <input value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="예: 매입, 매출, 외주, 용역..." style={inputStyle} />
          <button onClick={handleAdd} disabled={loading || !name.trim()}
            style={{ padding:'9px 16px', background: (loading || !name.trim()) ? '#aaa' : '#185FA5', color:'#fff',
              border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor: (loading || !name.trim()) ? 'not-allowed' : 'pointer' }}>
            등록
          </button>
        </div>
        {msg && <div style={{ fontSize:12, color:'#dc2626', marginBottom:10 }}>{msg}</div>}

        <div style={{ marginTop:12, border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
          {categories.length === 0 ? (
            <div style={{ padding:'24px 0', textAlign:'center', color:'#aaa', fontSize:13 }}>등록된 목록이 없습니다.</div>
          ) : (
            categories.map((c, i) => (
              <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8,
                padding:'8px 14px', borderBottom: i < categories.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <div style={{ display:'flex', flexDirection:'column' }}>
                  <button onClick={() => handleMove(i, -1)} disabled={i === 0}
                    style={{ background:'none', border:'none', fontSize:11, lineHeight:1, padding:'2px 4px',
                      color: i === 0 ? '#ddd' : '#888', cursor: i === 0 ? 'not-allowed' : 'pointer' }}>▲</button>
                  <button onClick={() => handleMove(i, 1)} disabled={i === categories.length - 1}
                    style={{ background:'none', border:'none', fontSize:11, lineHeight:1, padding:'2px 4px',
                      color: i === categories.length - 1 ? '#ddd' : '#888', cursor: i === categories.length - 1 ? 'not-allowed' : 'pointer' }}>▼</button>
                </div>

                {editingId === c.id ? (
                  <input value={editingName} onChange={e => setEditingName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && saveEdit(c.id)}
                    autoFocus
                    style={{ flex:1, padding:'6px 10px', border:'1px solid #185FA5', borderRadius:6, fontSize:14, outline:'none' }} />
                ) : (
                  <span style={{ flex:1, fontSize:14, color:'#1a1a1a' }}>{c.name}</span>
                )}

                <div style={{ display:'flex', gap:10, flexShrink:0 }}>
                  {editingId === c.id ? (
                    <>
                      <button onClick={() => saveEdit(c.id)} style={{ background:'none', border:'none', color:'#185FA5', fontSize:13, cursor:'pointer' }}>저장</button>
                      <button onClick={cancelEdit} style={{ background:'none', border:'none', color:'#888', fontSize:13, cursor:'pointer' }}>취소</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(c)} style={{ background:'none', border:'none', color:'#185FA5', fontSize:13, cursor:'pointer' }}>수정</button>
                      <button onClick={() => handleDelete(c.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:13, cursor:'pointer' }}>삭제</button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
